import React from 'react';
import styled, {css, keyframes} from 'styled-components';
import {useTranslation} from 'react-i18next';
import {FaMapSigns, FaShoppingCart, FaWrench} from 'react-icons/fa';
import {useInView} from 'react-intersection-observer';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Container = styled.div`
    max-width: 1000px;
    width: calc(100% - 40px);
    margin: 0 auto;
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    gap: 40px;
`;

const Title = styled.h2`
    color: ${props => props.theme.colors.primary};
    text-align: center;
    font-size: 2rem;
    margin-bottom: 20px;
    animation: ${fadeIn} 0.5s ease-out forwards;

    @media (max-width: 768px) {
        font-size: 1.8rem;
    }
`;

const ServiceCard = styled.div`
    background: linear-gradient(135deg, ${props => props.theme.colors.background} 0%, ${props => props.theme.colors.primary}22 100%);
    border-radius: 15px;
    padding: 20px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    border-top: 3px solid ${props => props.theme.colors.primary};
    display: flex;
    flex-direction: column;
    gap: 15px;
    opacity: 0;
    transition: opacity 0.5s ease-out;
    ${props =>
            props.isVisible &&
            css`
                animation: ${fadeIn} 0.8s ease-out forwards;
            `} @media (
    max-width: 768px) {
    padding: 15px;
}
`;

const ServiceImage = styled.img`
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    border: 1px solid ${props => props.theme.colors.primary}33;

    @media (max-width: 768px) {
        height: 150px;
    }
`;

const ServiceIcon = styled.div`
    font-size: 2.5rem;
    color: ${props => props.theme.colors.primary};

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const ServiceContent = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
`;

const ServiceTitle = styled.h3`
    color: ${props => props.theme.colors.primary};
    font-size: 1.5rem;
    margin: 0;
    font-weight: 600;

    @media (max-width: 768px) {
        font-size: 1.3rem;
    }
`;

const ServiceDescription = styled.p`
    color: ${props => props.theme.colors.text};
    font-size: 1.1rem;
    margin: 0;
    line-height: 1.6;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const NoServices = styled.p`
    color: ${props => props.theme.colors.secondary};
    font-size: 1.2rem;
    text-align: center;
    margin: 40px 0;
`;

// Component for rendering a single service card with useInView
const ServiceCardComponent = ({service, index, getServiceIcon, t, servicesImages}) => {
    const [ref, inView] = useInView({threshold: 0.2, triggerOnce: true});

    return (
        <ServiceCard ref={ref} isVisible={inView}>
            <ServiceImage
                src={servicesImages[index % servicesImages.length].url}
                alt={`Service Image ${index + 1}`}
            />
            <ServiceContent>
                <ServiceIcon>{getServiceIcon(service.serviceType)}</ServiceIcon>
                <div>
                    <ServiceTitle>{service.title || t('serviceTitle', {defaultValue: 'Servizio'})}</ServiceTitle>
                    <ServiceDescription>
                        {service.description || t('serviceDescription', {defaultValue: 'Descrizione non disponibile'})}
                    </ServiceDescription>
                </div>
            </ServiceContent>
        </ServiceCard>
    );
};

const Services = ({rentUser, config}) => {
    const {t} = useTranslation();

    const getServiceIcon = (serviceType) => {
        switch (serviceType) {
            case 'REPAIR':
                return <FaWrench/>;
            case 'SHOP':
                return <FaShoppingCart/>;
            case 'GUIDE':
                return <FaMapSigns/>;
            default:
                return null;
        }
    };

    // Use servicesImages from config, fallback to placeholder if empty
    const servicesImages = config?.servicesImages?.length
        ? config.servicesImages
        : [{url: 'https://via.placeholder.com/800x200'}];

    if (!config?.isServicesPageAvailable) {
        return <NoServices>{t('servicesNotAvailable', {defaultValue: 'Nessun servizio disponibile'})}</NoServices>;
    }

    return (
        <Container>
            <Title>{t('ourServices')}</Title>
            {config?.rentServices?.length > 0 ? (
                config.rentServices.map((service, index) => (
                    <ServiceCardComponent
                        key={index}
                        service={service}
                        index={index}
                        getServiceIcon={getServiceIcon}
                        t={t}
                        servicesImages={servicesImages}
                    />
                ))
            ) : (
                <NoServices>{t('noServices', {defaultValue: 'Nessun servizio disponibile'})}</NoServices>
            )}
        </Container>
    );
};

export default Services;
